import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import rrulePlugin from '@fullcalendar/rrule';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Button, Dimmer, Loader, Message, Modal, Segment, Image } from 'semantic-ui-react';
import UserContext from '../../../contexts/UserContext';
import moment from 'moment-timezone';
import './style.less';

import { fetchAllCommunityAnnouncements } from '../../services/Announcements';
import { useFacility } from '../../contexts/facility/FacilityContext';
import sendToast from '../../../utils/Toast';
import { Announcement } from '../../types/Announcement';
import useMediaQuery from '../../hooks/useMediaQuery';
import CompactMonthCalendar, {
    CompactCalendarEvent,
} from '../../components/CompactMonthCalendar';
import {
    expandCalendarEventsForRange,
    mapAnnouncementsToFullCalendarEvents,
} from './announcementCalendarUtils';

interface AnnouncementEvent {
    title: string;
    creatorInfo: any;
    ResidentNames: string[];
    start: Date;
    end: Date;
    id: string;
    rrRule?: any;
    exDate: string[];
}

/** Switch from FullCalendar to CompactMonthCalendar at this width and below. */
const COMPACT_CALENDAR_QUERY = '(max-width: 1024px)';

const Announcements = () => {
    const isCompact = useMediaQuery(COMPACT_CALENDAR_QUERY);
    const { facilityTimezone, loading: loadingFacilityInfo } = useFacility();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [selectedDateRange, setSelectedDateRange] = useState({
        startDateTimeString: moment().startOf('month').format('YYYY-MM-DDTHH:mm:ss'),
        endDateTimeString: moment().endOf('month').format('YYYY-MM-DDTHH:mm:ss'),
    });
    const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
    const [announcementsModalOpen, setAnnouncementsModalOpen] = useState<boolean>(false);
    const [selectedEvent, setSelectedEvent] = useState<AnnouncementEvent>();
    /** Month on display, owned here so it survives the compact/desktop swap. */
    const [viewMonth, setViewMonth] = useState(() => moment().startOf('month').toDate());
    // Collect resident information that corresponds to the logged in neighbors web user from UserContext
    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only allows one resident to be linked to one account

    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoadingAnnouncements(true);
            if (selectedDateRange.startDateTimeString && selectedDateRange.endDateTimeString && facilityTimezone) {
                const startDateTimeISO = moment
                    .tz(selectedDateRange.startDateTimeString, facilityTimezone)
                    .toISOString();
                const endDateTimeISO = moment.tz(selectedDateRange.endDateTimeString, facilityTimezone).toISOString();
                try {
                    // Unchanged from HEAD. excludeScheduled drops triggerTimestampISO rows.
                    // Recurrence expansion still applies to returned logs that carry Recurrence.
                    // Do not set excludeScheduled false to “make compact recurrence useful.”
                    const announcementsResp = await fetchAllCommunityAnnouncements(
                        correspondingSpeak2Resident.Facility,
                        {
                            startTimestampISO: startDateTimeISO,
                            endTimestampISO: endDateTimeISO,
                            residentId: correspondingSpeak2Resident._id,
                            excludeScheduled: true,
                        },
                    );
                    setAnnouncements(announcementsResp);
                } catch (error) {
                    sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Announcements');
                }
            }
            setLoadingAnnouncements(false);
        };
        fetchAnnouncements();
    }, [selectedDateRange.startDateTimeString, selectedDateRange.endDateTimeString, facilityTimezone]);

    const handleMonthChange = useCallback(({ start, end }) => {
        const startDateTimeString = moment(start).format('YYYY-MM-DDTHH:mm:ss');
        const endDateTimeString = moment(end).format('YYYY-MM-DDTHH:mm:ss');
        setSelectedDateRange({
            startDateTimeString,
            endDateTimeString,
        });
        // Both views report the Sunday-aligned grid, whose 8th day always lands in the
        // month on display. Holding it here keeps the month stable when the breakpoint
        // swaps one calendar for the other.
        const anchor = moment(start).add(7, 'days');
        const nextViewMonth = new Date(anchor.year(), anchor.month(), 1);
        setViewMonth((prev) =>
            prev.getTime() === nextViewMonth.getTime() ? prev : nextViewMonth,
        );
    }, []);

    const openAnnouncementModal = (payload: {
        title: string;
        creatorInfo: any;
        ResidentNames: string[];
        start: Date;
        end: Date;
        id: string;
        rrRule?: any;
        exDate?: string[];
    }) => {
        setAnnouncementsModalOpen(true);
        setSelectedEvent({
            title: payload.title,
            creatorInfo: payload.creatorInfo,
            ResidentNames: payload.ResidentNames,
            start: payload.start,
            end: payload.end,
            id: payload.id,
            rrRule: payload.rrRule,
            exDate: payload.exDate || [],
        });
    };

    const handleEventClick = (info) => {
        openAnnouncementModal({
            title: info.event.title,
            creatorInfo: info.event.extendedProps.creatorInfo,
            ResidentNames: info.event.extendedProps.ResidentNames,
            start: info.event.start,
            end: info.event.end,
            id: info.event.id,
            rrRule: info.event.extendedProps.rrule,
            exDate: info.event.extendedProps.exdate,
        });
    };

    const handleCompactEventClick = (event: CompactCalendarEvent) => {
        openAnnouncementModal({
            title: event.title,
            creatorInfo: event.meta && event.meta.creatorInfo,
            ResidentNames: (event.meta && event.meta.ResidentNames) || [],
            start: event.start,
            end: event.end,
            id: event.id,
            rrRule: event.meta && event.meta.rrRule,
            exDate: (event.meta && event.meta.exDate) || [],
        });
    };

    const closeAnnouncementsModal = () => {
        setAnnouncementsModalOpen(false);
    };

    const fullCalendarEvents = useMemo(
        () => mapAnnouncementsToFullCalendarEvents(announcements, facilityTimezone),
        [announcements, facilityTimezone],
    );

    const compactEvents = useMemo(() => {
        if (!isCompact) return [];
        return expandCalendarEventsForRange(
            fullCalendarEvents,
            new Date(selectedDateRange.startDateTimeString),
            new Date(selectedDateRange.endDateTimeString),
        );
    }, [
        isCompact,
        fullCalendarEvents,
        selectedDateRange.startDateTimeString,
        selectedDateRange.endDateTimeString,
    ]);

    if (!correspondingSpeak2Resident) {
        return <Message content="Invalid User. Please contact support" />;
    }

    return (
        <>
            {isCompact ? (
                <div className="neighbors-compact-calendar-page">
                    <CompactMonthCalendar
                        events={compactEvents}
                        initialMonth={viewMonth}
                        onDatesSet={handleMonthChange}
                        onEventClick={handleCompactEventClick}
                        loading={loadingAnnouncements || loadingFacilityInfo}
                        itemNoun="announcement"
                    />
                </div>
            ) : (
                <div className="announcements-full-calendar">
                    <Segment className="announcements-full-calendar-card">
                        <div className="menu-calendar-view">
                            <Dimmer active={loadingAnnouncements || loadingFacilityInfo} inverted>
                                <Loader content="Loading Announcements" />
                            </Dimmer>
                            <FullCalendar
                                plugins={[dayGridPlugin, interactionPlugin, rrulePlugin]}
                                initialView="dayGridMonth"
                                initialDate={viewMonth}
                                headerToolbar={{
                                    right: 'today prev,next',
                                }}
                                buttonText={{ today: 'Today' }}
                                events={fullCalendarEvents}
                                datesSet={handleMonthChange}
                                eventClick={handleEventClick}
                                dayHeaderFormat={{ weekday: 'long' }}
                            />
                        </div>
                    </Segment>
                </div>
            )}
            {/* Modal for displaying event details */}
            <Modal open={announcementsModalOpen} onClose={closeAnnouncementsModal} className="announcements-details-modal">
                <Modal.Content>
                    <div className="announcement-card-content">
                        <Image
                            className="announcement-avatar"
                            src={(selectedEvent && selectedEvent.creatorInfo && selectedEvent.creatorInfo.icon) || '/avatar_placeholder.png'}
                        />
                        <div className="announcement-card-body">
                            {selectedEvent && selectedEvent.creatorInfo && (
                                <h3 className="announcement-title">
                                    {`Announcement from ${`${selectedEvent.creatorInfo.firstName} ${
                                        selectedEvent.creatorInfo.lastName
                                    }`.trim()}`}
                                </h3>
                            )}
                            {selectedEvent && selectedEvent.start && (
                                <p className="announcement-time">
                                    {moment(selectedEvent.start).format('h:mm A on MMM D, YYYY')} (
                                    {moment(selectedEvent.start).fromNow()})
                                </p>
                            )}
                        </div>
                    </div>
                    <p className="announcement-message announcement-message-full">
                        {(selectedEvent && selectedEvent.title) || ''}
                    </p>
                </Modal.Content>
                <Modal.Actions className="announcement-actions">
                    <Button color="red" size="small" onClick={closeAnnouncementsModal}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default Announcements;
